import React, { useState } from "react";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { 
  User, Building2, Briefcase, Award, 
  ChevronRight, ShieldCheck, CheckCircle2, 
  Cpu, Zap, Globe, 
  Users,
  Mail,
  Phone,
  Lock,
  MapPin,
  ArrowLeft
} from "lucide-react";

const SignupPage = () => {
const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
 const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    profileType: "", // 'individual' or 'organization'
    fullName: "",
    phone: "",
    organizationName: "",
    employeeCount: "1",
    address: "",
    taxId: "", // GST or VAT
    certifyingBody: "ASNT",
    ndtLevel: "Level II",
    ndtMethods: "",
    role: "admin", // Default for the person creating the account
    subscriptionTier: "starter"
  });

  const handleNext = () => setStep((prev) => prev + 1);
  const handleBack = () => setStep((prev) => prev - 1);

  // Step 1: Firebase Auth Creation
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      setStep(2);
    } catch (error) {
      alert(error.message);
    } finally { setLoading(false); }
  };

  // Final Step: Detailed Firestore Record
// Final Step: Detailed Firestore Record
const finalizeRegistration = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert("Authentication session expired. Please restart the process.");
      return;
    }
    
    setLoading(true);

    try {
      // 1. Sanitize the data
      // We explicitly remove passwords and separate profileType for logic checks
      const { password, confirmPassword, ...profileData } = formData;

      // 2. Define Organization Logic
      // If it's an organization, the user's UID becomes the "orgId" (Master Account)
      // If individual, the orgId is "personal" or same as UID
      const orgId = formData.profileType === "organization" ? user.uid : `ind_${user.uid}`;

      const finalPayload = {
        ...profileData,
        uid: user.uid,
        orgId: orgId, // Critical for multi-user data scoping later
        role: "admin", // The registrant is always the primary admin
        email: user.email,
        createdAt: new Date().toISOString(),
        isVerified: false,
        accountStatus: "active",

        // --- NDT 4.0 SYSTEM DEFAULTS ---
        credits_remaining: 10,
        total_reports_generated: 0,
        
        storage: {
          usedBytes: 0,
          limitBytes: formData.profileType === "organization" ? 524288000 : 104857600, // 500MB for Org, 100MB for Ind
        },
        
        subscription: {
          tier: formData.employeeCount === "50+" ? "enterprise" : "starter",
          status: "trialing",
          startDate: new Date().toISOString(),
          expiryDate: null 
        },

        // --- COMPLIANCE & ONBOARDING ---
        requiresCustomOnboarding: formData.employeeCount === "50+" || formData.profileType === "organization",
        systemPreferences: {
          defaultStandard: "ASME SEC V", // Default starting standard
          unitSystem: "metric",
          autoSync: true
        }
      };

      // 3. Save to Firestore
      await setDoc(doc(db, "users", user.uid), finalPayload);

      // 4. If Organization, create a separate 'organizations' collection entry 
      // This allows you to list employees under this org later without querying all users
      if (formData.profileType === "organization") {
        await setDoc(doc(db, "organizations", orgId), {
          orgName: formData.organizationName,
          adminUid: user.uid,
          taxId: formData.taxId || "Pending",
          createdAt: new Date().toISOString(),
          memberCount: 1,
          maxSeats: parseInt(formData.employeeCount.split('-')[1]) || 50
        });
      }

      // 5. Cleanup and Redirect
      localStorage.removeItem('evalndt_free_reports');
      
      // Use a soft redirect or a state push if using react-router
      window.location.href = "/dashboard";
      
    } catch (err) {
      console.error("Critical Registration Failure:", err);
      alert(`System Calibration Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
 <div className="min-h-screen bg-white flex flex-col lg:flex-row">
  
  {/* LEFT SIDE: BRAND & IMPACT (KEPT EXACTLY AS PROVIDED) */}
  <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 items-center justify-center p-12 overflow-hidden">
    <div className="absolute inset-0 z-0 opacity-40">
        <img 
            src="https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&q=80" 
            alt="Industrial Inspection" 
            className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/40 to-slate-900" />
    </div>

    <div className="relative z-10 max-w-lg">
      <div className="flex items-center gap-3 mb-8">
        <div className="p-3 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
          <Cpu className="text-white" size={32} />
        </div>
        <h1 className="text-3xl font-black text-white tracking-tighter italic">EvalNDT <span className="text-indigo-400">4.0</span></h1>
      </div>

      <h2 className="text-5xl font-black text-white leading-tight mb-6">
        Precision Intelligence for <span className="text-indigo-400">Advanced NDT.</span>
      </h2>
      
      <p className="text-slate-300 text-lg mb-12 font-medium">
        Join the elite network of inspectors utilizing the OmniScan X4 ecosystem for automated reporting and real-time data integrity.
      </p>

      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="mt-1 bg-indigo-500/20 p-2 rounded-lg"><Zap size={20} className="text-indigo-400" /></div>
          <div>
            <h4 className="text-white font-bold">Automated Report Generation</h4>
            <p className="text-slate-400 text-sm">Convert raw scan data into professional headers in seconds.</p>
          </div>
        </div>
        <div className="flex items-start gap-4">
          <div className="mt-1 bg-indigo-500/20 p-2 rounded-lg"><ShieldCheck size={20} className="text-indigo-400" /></div>
          <div>
            <h4 className="text-white font-bold">Code Compliance Guaranteed</h4>
            <p className="text-slate-400 text-sm">Built-in validation for ASME, API, and ISO standards.</p>
          </div>
        </div>
      </div>
    </div>
    
    <div className="absolute bottom-12 left-12 flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md rounded-full border border-white/10">
        <Globe size={14} className="text-indigo-400" />
        <span className="text-xs text-slate-300 font-bold uppercase tracking-widest">Global Inspection Standard</span>
    </div>
  </div>

  {/* RIGHT SIDE: AUTHENTICATION FLOW (REFINED & PROPER) */}
<div className="w-full lg:w-1/2 flex items-center justify-center p-6 md:p-12 lg:p-20 bg-slate-50 min-h-screen">
      <div className="w-full max-w-xl bg-white p-8 md:p-12 rounded-[2.5rem] shadow-xl border border-slate-100">
        
        {/* Progress Header */}
        <div className="mb-10">
          <div className="flex justify-between items-end mb-4">
            <div>
              <span className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em]">Phase 0{step} / 03</span>
              <h3 className="text-2xl font-black text-slate-900 tracking-tight">
                {step === 1 && "Security Initialization"}
                {step === 2 && (formData.profileType === "" ? "Select Account Type" : "Entity Configuration")}
                {step === 3 && "Technical Accreditation"}
              </h3>
            </div>
          </div>
          <div className="flex gap-2 h-1.5">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`flex-1 rounded-full transition-all duration-700 ${step >= i ? 'bg-indigo-600' : 'bg-slate-100'}`} />
            ))}
          </div>
        </div>

 {/* STEP 1: AUTHENTICATION */}
{step === 1 && (
  <form 
    onSubmit={handleCreateAccount} 
    className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500"
  >
    <div className="space-y-2">
      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
        Work Email
      </label>
      <div className="relative">
        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
        <input 
          type="email" 
          required 
          className="w-full pl-12 pr-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-indigo-600 focus:bg-white transition-all font-bold text-sm" 
          placeholder="name@company.com" 
          onChange={(e) => setFormData({...formData, email: e.target.value})} 
        />
      </div>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <div className="space-y-2">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
          Password
        </label>
        <div className="relative">
          <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
          <input 
            type="password" 
            required 
            minLength={6}
            className="w-full pl-12 pr-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-indigo-600 focus:bg-white transition-all font-bold text-sm" 
            placeholder="••••••••" 
            onChange={(e) => setFormData({...formData, password: e.target.value})} 
          />
        </div>
      </div>
      <div className="space-y-2">
        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
          Confirm
        </label>
        <input 
          type="password" 
          required 
          className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-indigo-600 focus:bg-white transition-all font-bold text-sm" 
          placeholder="••••••••" 
          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})} 
        />
      </div>
    </div>

    <button 
      type="submit"
      disabled={loading}
      className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs tracking-[0.2em] hover:bg-indigo-700 transition-all shadow-lg flex items-center justify-center gap-2 mt-4 uppercase disabled:opacity-70 disabled:cursor-not-allowed"
    >
      {loading ? (
        "Verifying Protocol..."
      ) : (
        <>
          Initialize Secure Access <ChevronRight size={16} />
        </>
      )}
    </button>
  </form>
)}

        {/* STEP 2: PROFILE SELECTION & DETAILS */}
        {step === 2 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
            {/* Initial Selection Choice */}
            {formData.profileType === "" ? (
              <div className="grid grid-cols-1 gap-4 py-4">
                <button 
                  onClick={() => setFormData({...formData, profileType: 'individual'})}
                  className="group flex items-center gap-6 p-6 border-2 border-slate-100 rounded-[2rem] hover:border-indigo-600 hover:bg-indigo-50/30 transition-all text-left"
                >
                  <div className="p-4 bg-slate-50 rounded-2xl group-hover:bg-indigo-100 text-slate-400 group-hover:text-indigo-600 transition-colors">
                    <User size={32} />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-900 uppercase text-xs tracking-widest">Individual Professional</h4>
                    <p className="text-[10px] text-slate-500 font-bold mt-1">Freelance operators or independent contractors.</p>
                  </div>
                </button>

                <button 
                  onClick={() => setFormData({...formData, profileType: 'organization'})}
                  className="group flex items-center gap-6 p-6 border-2 border-slate-100 rounded-[2rem] hover:border-indigo-600 hover:bg-indigo-50/30 transition-all text-left"
                >
                  <div className="p-4 bg-slate-50 rounded-2xl group-hover:bg-indigo-100 text-slate-400 group-hover:text-indigo-600 transition-colors">
                    <Building2 size={32} />
                  </div>
                  <div>
                    <h4 className="font-black text-slate-900 uppercase text-xs tracking-widest">Organization Entity</h4>
                    <p className="text-[10px] text-slate-500 font-bold mt-1">Corporate groups, labs, or inspection firms.</p>
                  </div>
                </button>
              </div>
            ) : (
              /* Profile Details Based on Selection */
              <div className="space-y-5">
                <div className="flex items-center justify-between mb-2">
                  <button onClick={() => setFormData({...formData, profileType: ""})} className="flex items-center gap-2 text-[10px] font-black text-indigo-600 uppercase">
                    <ArrowLeft size={14} /> Change Type
                  </button>
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-[9px] font-black rounded-full uppercase tracking-widest">
                    {formData.profileType} mode
                  </span>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Legal Name</label>
                    <input type="text" placeholder="First & Last Name" className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none focus:border-indigo-600 transition-all"
                      onChange={(e) => setFormData({...formData, fullName: e.target.value})} />
                  </div>

                  {formData.profileType === 'organization' && (
                    <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Company Entity Name</label>
                        <input type="text" placeholder="Official Registered Name" className="w-full px-5 py-4 bg-white border border-indigo-100 rounded-2xl font-bold text-sm outline-none focus:border-indigo-600 shadow-sm"
                          onChange={(e) => setFormData({...formData, organizationName: e.target.value})} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Workforce</label>
                          <select className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm"
                            onChange={(e) => setFormData({...formData, employeeCount: e.target.value})}>
                            <option value="1-5">1-5 Staff</option>
                            <option value="6-20">6-20 Staff</option>
                            <option value="21-50">21-50 Staff</option>
                            <option value="50+">50+ Enterprise</option>
                          </select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Tax ID / GST</label>
                          <input type="text" placeholder="Reg Number" className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm outline-none focus:border-indigo-600"
                            onChange={(e) => setFormData({...formData, taxId: e.target.value})} />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Contact Phone</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                        <input type="tel" placeholder="+XX XXXXX" className="w-full pl-10 pr-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm"
                          onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Base Location</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                        <input type="text" placeholder="City, Country" className="w-full pl-10 pr-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm"
                          onChange={(e) => setFormData({...formData, address: e.target.value})} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4 pt-2">
                  <button onClick={handleBack} className="px-8 py-5 bg-slate-100 text-slate-500 rounded-2xl font-black text-xs uppercase tracking-widest">Back</button>
                  <button onClick={handleNext} className="flex-1 py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs tracking-[0.2em] shadow-lg hover:bg-indigo-700 transition-all uppercase flex items-center justify-center gap-2">
                    Professional Credentials <ChevronRight size={16}/>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 3: NDT SPECIALIZATION */}
        {step === 3 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-500">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Certifying Body</label>
                 <select className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm"
                  onChange={(e) => setFormData({...formData, certifyingBody: e.target.value})}>
                  <option>ASNT</option><option>PCN (BINDT)</option><option>ISO 9712</option><option>CSWIP</option>
                </select>
              </div>
              <div className="space-y-2">
                 <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">NDT Ranking</label>
                 <select className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl font-bold text-sm"
                  onChange={(e) => setFormData({...formData, ndtLevel: e.target.value})}>
                  <option>Level I</option><option>Level II</option><option>Level III</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Methods & Specialization</label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={18} />
                <input type="text" placeholder="e.g. PAUT, TOFD, UT, MT, PT" className="w-full pl-12 pr-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:border-indigo-600 font-bold text-sm"
                  onChange={(e) => setFormData({...formData, ndtMethods: e.target.value})} />
              </div>
            </div>

            <div className="p-6 bg-indigo-50/50 rounded-3xl border border-indigo-100 flex gap-4 items-start">
              <ShieldCheck className="text-indigo-600 shrink-0 mt-1" size={20} />
              <div className="space-y-1">
                <p className="text-[11px] text-indigo-900 font-black uppercase tracking-widest">Master Admin Privileges</p>
                <p className="text-[10px] text-indigo-700/70 font-bold leading-relaxed">
                  You are registering as the <span className="text-indigo-900 uppercase">primary account holder</span>. You will have full authority over billing, reporting standards, and user permissions.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <button onClick={handleBack} className="px-8 py-5 bg-slate-100 text-slate-500 rounded-2xl font-black text-xs uppercase tracking-widest">Back</button>
              <button onClick={finalizeRegistration} className="flex-1 py-6 bg-slate-900 text-white rounded-2xl font-black text-xs tracking-[0.3em] shadow-2xl hover:bg-black transition-all">
                {loading ? "CONFIGURING SYSTEM..." : "ACTIVATE EVALNDT ENGINE"}
              </button>
            </div>
          </div>
        )}

        {/* Global Compliance Footer */}
        <div className="mt-10 flex items-center justify-center gap-3 grayscale opacity-30">
          <Globe size={12} className="text-slate-400" />
          <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Compliant with ASME V & API Standards</span>
        </div>
      </div>
    </div>
</div>
  );
};

export default SignupPage;